import { Component, DestroyRef, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { Contact, ContactService, Person, PersonsService } from '@eadsysteme/contactclient-v1';
import { SessionService } from '../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, Observable, zip } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatFabButton } from '@angular/material/button';
import { SnackbarService } from '../../../services/snackbar.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { fadeInUp400ms } from '../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../@vex/animations/stagger.animation';
import { finalize } from 'rxjs/operators';
import { AddEmailDialogComponent } from './add-email-dialog/add-email-dialog.component';
import { ContactWithArea } from '../../../util/ContactWithArea';

@Component({
	selector: 'vex-document-transfer-settings',
	imports: [FormsModule, MatFormField, MatLabel, ReactiveFormsModule, TranslocoDirective, MatOption, MatSelect, MatIcon, MatFabButton, MatProgressSpinner],
	templateUrl: './document-transfer-settings.component.html',
	styleUrl: './document-transfer-settings.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class DocumentTransferSettingsComponent implements OnInit {
	private sessionService = inject(SessionService);
	private contactService = inject(ContactService);
	private dialog = inject(MatDialog);
	private snackbar = inject(SnackbarService);
	private transloco = inject(TranslocoService);
	private personService = inject(PersonsService);

	private readonly _document = 'Dokumenten';
	private destroyRef = inject(DestroyRef);

	isMobile: boolean;
	protected propertyManagementContacts: WritableSignal<Contact[]> = signal([]);
	protected finishedLoading = signal(false);
	protected finishedLoadingMails = signal(false);

	propertyManagementIds: ContactWithArea[];

	protected mailsByContact: Map<Contact, string[]> = new Map();
	protected mailsToUse: Map<string, FormControl<string>> = new Map();
	private personsByContact: Map<Contact, Person[]> = new Map();

	ngOnInit() {
		this.propertyManagementIds = this.sessionService.getPropertyManagementIdsAndAreas();

		const getContacts = this.propertyManagementIds.map((cpm) => {
			return this.contactService.getContactById(cpm.area, cpm.id);
		});
		const getContacts$: Observable<Array<any>> = zip(getContacts);
		this.loadContacts(getContacts$);
	}

	private loadContacts(getContacts$: Observable<Array<any>>) {
		getContacts$
			.pipe(
				finalize(() => this.finishedLoading.set(true)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: async (contacts: Contact[]) => {
					if (!(contacts.length > 0)) {
						return;
					}
					contacts = contacts.map((c) => {
						c.area = this.propertyManagementIds.find((cpm) => cpm.id === c.id)?.area;
						return c;
					});

					this.propertyManagementContacts.set(contacts);

					this.finishedLoadingMails.set(false);
					for (const contact of contacts) {
						await firstValueFrom(this.personService.getPersons(this.sessionService.effectiveArea, contact.accountNumber)).then((persons) => {
							this.collectMailsPerContact(persons, contact);
						});
					}
					this.finishedLoadingMails.set(true);
				},
			});
	}

	private collectMailsPerContact(persons: Array<Person>, contact: Contact) {
		this.personsByContact.set(contact, persons);
		let contactPersonMails = persons.filter((person) => person.emails != null && person.emails.length > 0).map((person) => person.emails[0]); //collect mails of persons
		if (contact.email) {
			contactPersonMails.push(contact.email); // add general email
		}
		contactPersonMails = [...new Set(contactPersonMails)]; //remove Duplicates
		if (contactPersonMails.length > 0) {
			this.mailsByContact.set(contact, contactPersonMails);
			const docTransferContact = persons.find((currentPerson) =>
				currentPerson.names.some((name) => name.fullName?.includes(this._document) || name.given?.includes(this._document))
			);
			this.mailsToUse.set(contact.id, new FormControl(docTransferContact?.emails[0] ?? ''));
		} else {
			this.mailsByContact.set(contact, []);
			this.mailsToUse.set(contact.id, new FormControl(''));
		}
	}

	openAddEmailDialog(contact: Contact): void {
		const dialogRef = this.dialog.open(AddEmailDialogComponent, {
			width: '350px',
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result && typeof result === 'string') {
					const newEmail = result.trim();

					const emails = this.mailsByContact.get(contact);
					const exists = emails?.some((e) => e.toLowerCase() === newEmail.toLowerCase());
					if (!exists) {
						this.createOrUpdateEmailContact(emails, newEmail, contact);
					} else {
						this.snackbar.warn('errors.mailAlreadyExists');
					}
				}
			});
	}

	private createOrUpdateEmailContact(emails: string[] = [], newEmail: string, contact: Contact) {
		emails.push(newEmail);
		const personToNotify = this.personsByContact
			.get(contact)
			.find((currentPerson) => currentPerson.names?.some((name) => name.fullName?.includes(this._document) || name.given?.includes(this._document)));
		if (personToNotify) {
			this.updateEmailOfContact(personToNotify, newEmail, contact);
		} else {
			this.createNewDocTransferContact(contact, newEmail);
		}
	}

	setEmailToModel($event: MatSelectChange, contact: Contact) {
		const newEmail = $event.value;
		const persons = this.personsByContact.get(contact);
		const personToNotify = persons.find((currentPerson) =>
			currentPerson.names?.some((name) => name.fullName?.includes(this._document) || name.given?.includes(this._document))
		);
		if (personToNotify) {
			this.updateEmailOfContact(personToNotify, newEmail, contact);
		} else {
			this.createNewDocTransferContact(contact, newEmail);
		}
	}

	private updateEmailOfContact(personToNotify: Person, newEmail: string, contact: Contact) {
		personToNotify.emails = [];
		personToNotify.emails.push(newEmail);
		this.personService
			.updatePerson(contact.area, contact.accountNumber, personToNotify.id, personToNotify)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.snackbar.success(this.transloco.translate('documentTransfer.notificationMailChanged'));
					this.mailsToUse.set(contact.id, new FormControl(newEmail));
				},
			});
	}

	private createNewDocTransferContact(contact: Contact, newEmail: string) {
		this.personService
			.addPerson(contact.area, contact.accountNumber, {
				emails: [newEmail],
				names: [{ given: this._document, family: 'Tausch' }],
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.snackbar.success(this.transloco.translate('documentTransfer.notificationMailChanged'));
					this.mailsToUse.set(contact.id, new FormControl(newEmail));
				},
			});
	}
}
